/**
 * OfferReview — public page listing agents visit via the secure token link
 * emailed by the "Send Offer to Listing Agent" flow. Shows them the offer PDF
 * and lets them submit comments in one shot.
 *
 * Mirrors the DisclosurePortal page pattern: no auth, token in the URL, backend
 * gates access by invite_token on the offer_listing_agent_comms row.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import ThirdPartyLogo from '../components/ThirdPartyLogo';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function OfferReview() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(false);

  // Accept-offer state
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [sellers, setSellers] = useState([{ name: '', email: '' }]);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState(null);

  // Broker-info form state (Phase 3 — appears after Accept click, before
  // seller form). Required: firm + license. Other fields optional.
  const [brokerInfo, setBrokerInfo] = useState({
    la_firm_name: '',
    la_broker_license_no: '',
    la_license_no: '',
    la_supervisor_name: '',
    la_supervisor_license_no: '',
    la_office_address: '',
    la_office_phone: '',
  });
  const [brokerSubmitting, setBrokerSubmitting] = useState(false);
  const [brokerError, setBrokerError] = useState(null);

  // Resend-to-sellers state (post-accept). resendOk is the ISO timestamp of
  // the most recent successful resend; we briefly show a confirmation pill
  // (cleared by setTimeout below) and then restore the button.
  const [resending, setResending] = useState(false);
  const [resendOk, setResendOk] = useState(null);
  const [resendError, setResendError] = useState(null);

  const fetchOffer = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/offer-review/${token}`);
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Could not load this page.'); return; }
      setData(d);
      if (d.comments_submitted_at) setDone(true);
    } catch (_) {
      setError('Could not load this page.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchOffer(); }, [fetchOffer]);

  const openPdf = () => {
    if (!data?.pdf_base64) { alert('PDF not available for this offer.'); return; }
    // Decode the base64 chunked to avoid the argument-count cap of atob+String.fromCharCode on
    // large PDFs. ~1MB PDF would overflow a single-shot decode on some browsers.
    const byteChars = atob(data.pdf_base64);
    const byteArr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArr], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const downloadPdf = () => {
    if (!data?.pdf_base64) { alert('PDF not available for this offer.'); return; }
    const byteChars = atob(data.pdf_base64);
    const byteArr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArr], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Offer_${(data.property_address || 'Doorly').replace(/[^a-z0-9]/gi, '_')}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const updateBroker = (field, value) => {
    setBrokerInfo((prev) => ({ ...prev, [field]: value }));
    if (brokerError) setBrokerError(null);
  };

  const submitBrokerInfo = async () => {
    const firm = brokerInfo.la_firm_name.trim();
    const brokerLic = brokerInfo.la_broker_license_no.trim();
    const lic = brokerInfo.la_license_no.trim();
    if (!firm) { setBrokerError('Listing Broker Firm is required.'); return; }
    if (!brokerLic) { setBrokerError('Listing Broker License Number is required.'); return; }
    if (!lic) { setBrokerError('Your License Number is required.'); return; }
    setBrokerSubmitting(true); setBrokerError(null);
    try {
      const r = await fetch(`${API}/api/offer-review/${token}/broker-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          la_firm_name: firm,
          la_broker_license_no: brokerLic,
          la_license_no: lic,
          la_supervisor_name: brokerInfo.la_supervisor_name.trim(),
          la_supervisor_license_no: brokerInfo.la_supervisor_license_no.trim(),
          la_office_address: brokerInfo.la_office_address.trim(),
          la_office_phone: brokerInfo.la_office_phone.trim(),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Could not submit broker info.');
      // Refresh — server now has broker_info_submitted_at; render flips
      // to the seller form on the next pass.
      fetchOffer();
    } catch (e) {
      setBrokerError(e.message || 'Could not submit broker info.');
    } finally { setBrokerSubmitting(false); }
  };

  const updateSeller = (idx, field, value) => {
    setSellers((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
    if (acceptError) setAcceptError(null);
  };
  const addSeller = () => {
    setSellers((prev) => (prev.length >= 4 ? prev : [...prev, { name: '', email: '' }]));
  };
  const removeSeller = (idx) => {
    setSellers((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  const submitAccept = async () => {
    const cleaned = sellers.map((s) => ({ name: s.name.trim(), email: s.email.trim() }));
    if (cleaned.some((s) => !s.name || !s.email)) {
      setAcceptError('Please fill in name and email for every seller.');
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (cleaned.some((s) => !emailRe.test(s.email))) {
      setAcceptError('One or more seller emails is not a valid email address.');
      return;
    }
    setAccepting(true); setAcceptError(null);
    try {
      const r = await fetch(`${API}/api/offer-review/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sellers: cleaned }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Acceptance failed');
      // Refresh so the accepted state renders from server-truthful data.
      fetchOffer();
    } catch (e) {
      setAcceptError(e.message || 'Acceptance failed. Please try again.');
    } finally { setAccepting(false); }
  };

  // Resend the seller PSA-pending notification emails. Allowed any number of
  // times. On success, render a "✓ Resent" confirmation pill for 5 seconds,
  // then restore the button. Errors render inline below the button and the
  // button stays available for retry.
  const handleResend = async () => {
    setResending(true);
    setResendError(null);
    setResendOk(null);
    try {
      const r = await fetch(`${API}/api/offer-review/${token}/resend-sellers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Resend failed.');
      setResendOk(d.resentAt || new Date().toISOString());
      setTimeout(() => setResendOk(null), 5000);
    } catch (e) {
      setResendError(e.message || 'Resend failed. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const submitComments = async () => {
    const trimmed = comments.trim();
    if (!trimmed) { setSubmitError('Please enter your comments before submitting.'); return; }
    setSubmitting(true); setSubmitError(null);
    try {
      const r = await fetch(`${API}/api/offer-review/${token}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comments: trimmed }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Submission failed');
      setDone(true);
      // Refresh so the confirmation card shows server-stamped timestamp
      fetchOffer();
    } catch (e) {
      setSubmitError(e.message || 'Submission failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#9ca3af' }}>
        <div style={{ width: 28, height: 28, border: '3px solid #e5e7eb', borderTopColor: '#3a6ad9', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 20 }}>
        <div style={{ maxWidth: 480, textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
          <div style={{ color: '#ff082f', fontSize: 16, fontWeight: 700, marginBottom: 8 }}>This link is no longer valid.</div>
          <div style={{ color: '#6b7280', fontSize: 14 }}>{error}</div>
        </div>
      </div>
    );
  }

  const submittedAt = data?.comments_submitted_at ? new Date(data.comments_submitted_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9', padding: '40px 20px' }}>
      <div style={{ maxWidth: 800, margin: '0 auto', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.08)' }}>

        {/* Header */}
        <ThirdPartyLogo band />
        <div style={{ background: '#3a6ad9', padding: '14px 36px', textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Offer Review</div>
        </div>

        {/* Property + offer context */}
        <div style={{ padding: '28px 36px 8px' }}>
          <p style={{ margin: '0 0 6px', fontSize: 13, color: '#6b7280' }}>
            Hi {data?.listing_agent_name || 'Listing Agent'},
          </p>
          <h1 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800, color: '#111827', lineHeight: 1.3 }}>
            {data?.property_address || 'Your Listing'}
          </h1>
          <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
            Offer submitted by <strong style={{ color: '#111827' }}>Go Doorly, LLC dba Doorly</strong>
          </p>
        </div>

        {/* Actions panel */}
        <div style={{ padding: '20px 36px', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button
              onClick={openPdf}
              disabled={!data?.pdf_base64}
              style={{
                padding: '12px 24px',
                background: data?.pdf_base64 ? '#acf44e' : '#e5e7eb',
                color: data?.pdf_base64 ? '#1a1a1a' : '#9ca3af',
                border: 'none', borderRadius: 10,
                fontWeight: 800, fontSize: 14,
                cursor: data?.pdf_base64 ? 'pointer' : 'not-allowed',
                display: 'inline-flex', alignItems: 'center', gap: 8,
              }}>
              View Offer
            </button>
            <button
              onClick={downloadPdf}
              disabled={!data?.pdf_base64}
              style={{
                padding: '10px 18px',
                background: 'transparent',
                color: data?.pdf_base64 ? '#3a6ad9' : '#9ca3af',
                border: `1.5px solid ${data?.pdf_base64 ? '#3a6ad9' : '#e5e7eb'}`, borderRadius: 10,
                fontWeight: 700, fontSize: 13,
                cursor: data?.pdf_base64 ? 'pointer' : 'not-allowed',
              }}>
              Download PDF
            </button>
          </div>
          {!data?.pdf_base64 && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#9f1239' }}>
              We're unable to locate the offer PDF. Please contact <a href="mailto:contracts@godoorly.com" style={{ color: '#3a6ad9' }}>contracts@godoorly.com</a>.
            </div>
          )}
        </div>

        {/* Comments from Doorly — only renders when the admin filled
            the "Comments to Listing Agent" field on the send-offer
            modal. Surfaces in the same green-accent style as the
            success state below so LAs visually associate it with the
            outbound Doorly message. */}
        {data?.comments_from_doorly && (
          <div style={{ padding: '20px 36px 0' }}>
            <div style={{ background: '#f4fee8', borderLeft: '4px solid #acf44e', borderRadius: '0 12px 12px 0', padding: '14px 18px' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#2d6a0a', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                Comments from Doorly
              </div>
              <div style={{ fontSize: 14, color: '#1a1a1a', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {data.comments_from_doorly}
              </div>
            </div>
          </div>
        )}

        {/* Comments panel */}
        <div style={{ padding: '24px 36px 32px' }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 800, color: '#111827' }}>Submit Comments</h2>

          {done ? (
            <>
              <div style={{ padding: 16, background: '#f4fee8', border: '2px solid #acf44e', borderRadius: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#2d6a0a', marginBottom: 8 }}>
                  ✓ Your comments have been received.
                </div>
                {submittedAt && (
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}>
                    Submitted {submittedAt}
                  </div>
                )}
                {data?.comments && (
                  <div style={{ fontSize: 13, color: '#111827', whiteSpace: 'pre-wrap', lineHeight: 1.6, padding: '12px 14px', background: '#fff', borderRadius: 8, border: '1px solid #d4f99a' }}>
                    {data.comments}
                  </div>
                )}
              </div>
              <p style={{ margin: 0, fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
                Comments already submitted — thank you. The Doorly team will follow up shortly.
              </p>
            </>
          ) : (
            <>
              <textarea
                value={comments}
                onChange={(e) => { setComments(e.target.value); if (submitError) setSubmitError(null); }}
                placeholder="Add any notes, requested changes, or questions here..."
                rows={7}
                maxLength={10000}
                disabled={submitting}
                style={{
                  width: '100%', padding: '14px 16px',
                  border: '1px solid #d1d5db', borderRadius: 12,
                  fontSize: 14, fontFamily: 'inherit', lineHeight: 1.6,
                  resize: 'vertical', outline: 'none', boxSizing: 'border-box',
                }}
              />
              {submitError && (
                <div style={{ marginTop: 10, padding: '10px 12px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: 8, fontSize: 13, color: '#ff082f' }}>
                  {submitError}
                </div>
              )}
              <button
                onClick={submitComments}
                disabled={submitting || !comments.trim()}
                style={{
                  marginTop: 14,
                  padding: '12px 28px',
                  background: submitting || !comments.trim() ? '#e5e7eb' : '#3a6ad9',
                  color: submitting || !comments.trim() ? '#9ca3af' : '#fff',
                  border: 'none', borderRadius: 10,
                  fontWeight: 800, fontSize: 14,
                  cursor: submitting || !comments.trim() ? 'not-allowed' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                }}>
                {submitting ? 'Submitting…' : 'Submit Comments'}
              </button>
              <p style={{ margin: '14px 0 0', fontSize: 12, color: '#9ca3af', lineHeight: 1.6 }}>
                The Doorly team will review your comments and follow up directly at the email address on file.
              </p>
            </>
          )}
        </div>

        {/* Accept Offer panel */}
        <div style={{ padding: '24px 36px 32px', borderTop: '1px solid #f3f4f6' }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 800, color: '#111827' }}>Accept Offer</h2>

          {data?.accepted_at ? (
            <div style={{ padding: 16, background: '#f4fee8', border: '2px solid #acf44e', borderRadius: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#2d6a0a', marginBottom: 8 }}>
                ✓ Offer Accepted
              </div>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                Thank you for accepting this offer. The Purchase and Sale Agreement will be sent to the seller(s)
                listed below for electronic signature. Once all parties have signed, you will receive a copy.
              </p>
              {Array.isArray(data?.sellers) && data.sellers.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.04, marginBottom: 6 }}>Sellers notified</div>
                  <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#111827', lineHeight: 1.7 }}>
                    {data.sellers.map((s, i) => (
                      <li key={i}>{s.name} &mdash; {s.email}</li>
                    ))}
                  </ul>
                </div>
              )}
              <p style={{ margin: 0, fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>
                Questions? Contact us at <a href="mailto:contracts@godoorly.com" style={{ color: '#3a6ad9' }}>contracts@godoorly.com</a> or 512-713-6016.
              </p>

              {/* Resend seller PSA notifications — always available in
                  accepted/psa_routing_initiated state. Listing agent can
                  trigger as many resends as needed. */}
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #d4f99a' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                  Didn't receive the email?
                </div>
                {resendOk ? (
                  <div style={{ padding: '10px 14px', background: '#f4fee8', border: '1px solid #acf44e', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#2d6a0a' }}>
                    ✓ Resent successfully — {new Date(resendOk).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </div>
                ) : (
                  <button
                    onClick={handleResend}
                    disabled={resending}
                    type="button"
                    style={{
                      padding: '9px 18px',
                      background: 'transparent',
                      color: resending ? '#9ca3af' : '#3a6ad9',
                      border: `1.5px solid ${resending ? '#e5e7eb' : '#3a6ad9'}`,
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: resending ? 'not-allowed' : 'pointer',
                    }}>
                    {resending ? 'Resending…' : 'Resend to Sellers'}
                  </button>
                )}
                {resendError && (
                  <div style={{ marginTop: 8, padding: '8px 12px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: 8, fontSize: 12, color: '#ff082f' }}>
                    {resendError}
                  </div>
                )}
              </div>
            </div>
          ) : !acceptOpen ? (
            <button
              onClick={() => setAcceptOpen(true)}
              style={{
                padding: '14px 32px',
                background: '#acf44e', color: '#111827',
                border: 'none', borderRadius: 12,
                fontSize: 15, fontWeight: 900,
                cursor: 'pointer',
              }}>
              {data?.broker_info_submitted_at ? 'Continue Accepting Offer' : 'Accept This Offer'}
            </button>
          ) : !data?.broker_info_submitted_at ? (
            // ── Phase 3 — broker info form (NEW). Required: firm + license.
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 24 }}>
              <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: '#111827' }}>
                Your offer has been accepted.
              </p>
              <p style={{ margin: '0 0 18px', fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
                Before we can route the Purchase and Sale Agreement for signatures, we need
                your broker information as required by TREC.
              </p>
              {[
                { key: 'la_firm_name', label: 'Listing Broker Firm', required: true, placeholder: 'Firm name' },
                { key: 'la_broker_license_no', label: 'Listing Broker License Number (TREC)', required: true, placeholder: 'Firm TREC license #' },
                { key: 'la_license_no', label: 'Your License Number (TREC)', required: true, placeholder: 'Your TREC license #' },
                { key: 'la_supervisor_name', label: 'Licensed Supervisor Name', required: false, placeholder: 'Optional' },
                { key: 'la_supervisor_license_no', label: 'Supervisor License Number', required: false, placeholder: 'Optional' },
                { key: 'la_office_address', label: 'Broker Office Address', required: false, placeholder: 'Optional' },
                { key: 'la_office_phone', label: 'Broker Office Phone', required: false, placeholder: 'Optional' },
              ].map(({ key, label, required, placeholder }) => (
                <div key={key} style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 5 }}>
                    {label}{required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
                  </label>
                  <input
                    type="text"
                    value={brokerInfo[key]}
                    onChange={(e) => updateBroker(key, e.target.value)}
                    placeholder={placeholder}
                    disabled={brokerSubmitting}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }}
                  />
                </div>
              ))}
              {brokerError && (
                <div style={{ marginTop: 4, marginBottom: 12, padding: '10px 12px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: 8, fontSize: 13, color: '#ff082f' }}>
                  {brokerError}
                </div>
              )}
              <button
                onClick={submitBrokerInfo}
                disabled={brokerSubmitting}
                style={{
                  marginTop: 4,
                  padding: '12px 28px',
                  background: brokerSubmitting ? '#e5e7eb' : '#3a6ad9',
                  color: brokerSubmitting ? '#9ca3af' : '#fff',
                  border: 'none', borderRadius: 10,
                  fontSize: 14, fontWeight: 800,
                  cursor: brokerSubmitting ? 'not-allowed' : 'pointer',
                }}>
                {brokerSubmitting ? 'Submitting…' : 'Continue →'}
              </button>
            </div>
          ) : (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18 }}>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                Now please provide the seller(s) information.
              </p>
              {sellers.map((s, idx) => (
                <div key={idx} style={{ marginBottom: 12, padding: 12, background: '#f9fafb', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.04 }}>Seller {idx + 1}</div>
                    {sellers.length > 1 && (
                      <button type="button" onClick={() => removeSeller(idx)}
                        style={{ background: 'none', border: 'none', color: '#ff082f', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                        Remove
                      </button>
                    )}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Full Legal Name</label>
                    <input type="text" value={s.name} onChange={(e) => updateSeller(idx, 'name', e.target.value)}
                      placeholder="Jane A. Smith"
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Email Address</label>
                    <input type="email" value={s.email} onChange={(e) => updateSeller(idx, 'email', e.target.value)}
                      placeholder="seller@example.com"
                      style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                </div>
              ))}
              {sellers.length < 4 && (
                <button type="button" onClick={addSeller}
                  style={{ marginBottom: 14, padding: '8px 14px', background: 'transparent', color: '#3a6ad9', border: '1.5px dashed #3a6ad9', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  + Add Seller
                </button>
              )}

              {acceptError && (
                <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: 8, fontSize: 13, color: '#ff082f' }}>
                  {acceptError}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={submitAccept} disabled={accepting}
                  style={{ flex: 1, padding: '12px 24px', background: accepting ? '#e5e7eb' : '#acf44e', color: accepting ? '#9ca3af' : '#111827', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 900, cursor: accepting ? 'not-allowed' : 'pointer' }}>
                  {accepting ? 'Submitting…' : 'Submit & Accept Offer'}
                </button>
                <button type="button" onClick={() => { setAcceptOpen(false); setAcceptError(null); }} disabled={accepting}
                  style={{ padding: '12px 18px', background: 'transparent', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: accepting ? 'not-allowed' : 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 36px', background: '#f9fafb', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: 11, color: '#9ca3af' }}>
            Go Doorly, LLC · NMLS #2764749 · Equal Housing Opportunity
          </p>
        </div>
      </div>
    </div>
  );
}
